import { ref, push, set, get } from 'firebase/database';
import { database } from '../firebase';

export interface ActivityLogEntry {
  timestamp: number;
  uid: string;
  actionTaken: string;
  actionSubject: string;
}

export class ActivityLogService {
  private recentActivities: Map<string, number> = new Map();
  private readonly DEDUPLICATION_WINDOW = 5000; // 5 seconds

  /**
   * Logs a user activity to the activity_log table in Firebase
   * @param entry - The activity log entry to save
   */
  async logActivity(entry: ActivityLogEntry): Promise<void> {
    try {
      // Create a deduplication key
      const dedupKey = `${entry.uid}-${entry.actionTaken}-${entry.actionSubject}`;
      const now = Date.now();
      
      // Check if we've logged this exact activity recently
      const lastLogged = this.recentActivities.get(dedupKey);
      if (lastLogged && (now - lastLogged) < this.DEDUPLICATION_WINDOW) {
        console.log('🔄 Skipping duplicate activity log:', entry.actionTaken, entry.actionSubject);
        return;
      }
      
      // Update the recent activities map
      this.recentActivities.set(dedupKey, now);
      
      // Clean up old entries from the map (keep only last 100 entries)
      if (this.recentActivities.size > 100) {
        const entries = Array.from(this.recentActivities.entries());
        const cutoff = now - this.DEDUPLICATION_WINDOW;
        this.recentActivities.clear();
        entries.forEach(([key, timestamp]) => {
          if (timestamp > cutoff) {
            this.recentActivities.set(key, timestamp);
          }
        });
      }
      
      const activityLogRef = ref(database, 'activity_log');
      const newActivityRef = push(activityLogRef);
      await set(newActivityRef, entry);
      console.log('Activity logged successfully:', entry);
    } catch (error) {
      console.error('Error logging activity:', error);
      // Don't throw the error to avoid breaking the main flow
      // Activity logging should be non-blocking
    }
  }

  /**
   * Logs a user login activity
   * @param uid - The user ID
   */
  async logUserLogin(uid: string): Promise<void> {
    // Check if this is a new user (first time logging in)
    const isNewUser = await this.isNewUser(uid);
    
    const loginEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: isNewUser ? 'New User Login' : 'Login',
      actionSubject: 'Student Web App'
    };
    
    await this.logActivity(loginEntry);
  }

  /**
   * Checks if a user is new (has no existing data in the database)
   * @param uid - The user ID
   * @returns Promise<boolean> - true if new user, false if existing user
   */
  private async isNewUser(uid: string): Promise<boolean> {
    try {
      const userRef = ref(database, `user/${uid}`);
      const snapshot = await get(userRef);
      return !snapshot.exists();
    } catch (error) {
      console.error('Error checking if user is new:', error);
      // Default to treating as existing user if we can't determine
      return false;
    }
  }

  /**
   * Logs a demo login activity
   */
  async logDemoLogin(): Promise<void> {
    const loginEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: 'demo-user',
      actionTaken: 'Login',
      actionSubject: 'Student Web App (Demo)'
    };
    
    await this.logActivity(loginEntry);
  }

  /**
   * Logs a user sign-up activity
   * @param uid - The user ID
   */
  async logUserSignUp(uid: string): Promise<void> {
    const signUpEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'Sign Up',
      actionSubject: 'Student Web App'
    };
    
    await this.logActivity(signUpEntry);
  }

  /**
   * Logs a profile update activity
   * @param uid - The user ID
   * @param updatedFields - The fields that were updated in the profile (optional)
   */
  async logProfileUpdate(uid: string, updatedFields?: string): Promise<void> {
    const profileUpdateEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'Updated Profile',
      actionSubject: updatedFields || 'Required Profile Data'
    };
    
    await this.logActivity(profileUpdateEntry);
  }

  /**
   * Logs a grade addition activity
   * @param uid - The user ID
   * @param courseCount - The total number of courses with grades added
   */
  async logGradeAddition(uid: string, courseCount: number): Promise<void> {
    const gradeAdditionEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'Added Grades',
      actionSubject: `${courseCount} courses with grades`
    };
    
    await this.logActivity(gradeAdditionEntry);
  }

  /**
   * Logs a school pageview activity
   * @param uid - The user ID
   * @param schoolId - The school ID being viewed
   */
  async logSchoolPageview(uid: string, schoolId: string): Promise<void> {
    const schoolPageviewEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'School Pageview',
      actionSubject: schoolId
    };
    
    await this.logActivity(schoolPageviewEntry);
  }

  /**
   * Logs a program pageview activity
   * @param uid - The user ID
   * @param programId - The program ID being viewed
   */
  async logProgramPageview(uid: string, programId: string): Promise<void> {
    const programPageviewEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'Program Pageview',
      actionSubject: programId
    };
    
    await this.logActivity(programPageviewEntry);
  }

  /**
   * Logs a school added to favorites activity
   * @param uid - The user ID
   * @param schoolId - The school ID being added to favorites
   */
  async logSchoolAddedToFavorites(uid: string, schoolId: string): Promise<void> {
    const schoolFavoriteEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'Added School to Favourites',
      actionSubject: schoolId
    };
    
    await this.logActivity(schoolFavoriteEntry);
  }

  /**
   * Logs a school removed from favorites activity
   * @param uid - The user ID
   * @param schoolId - The school ID being removed from favorites
   */
  async logSchoolRemovedFromFavorites(uid: string, schoolId: string): Promise<void> {
    const schoolUnfavoriteEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'Removed School from Favourites',
      actionSubject: schoolId
    };
    
    await this.logActivity(schoolUnfavoriteEntry);
  }

  /**
   * Logs a program added to favorites activity
   * @param uid - The user ID
   * @param programId - The program ID being added to favorites
   */
  async logProgramAddedToFavorites(uid: string, programId: string): Promise<void> {
    const programFavoriteEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'Added Program to Favourites',
      actionSubject: programId
    };
    
    await this.logActivity(programFavoriteEntry);
  }

  /**
   * Logs a program removed from favorites activity
   * @param uid - The user ID
   * @param programId - The program ID being removed from favorites
   */
  async logProgramRemovedFromFavorites(uid: string, programId: string): Promise<void> {
    const programUnfavoriteEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'Removed Program from Favourites',
      actionSubject: programId
    };
    
    await this.logActivity(programUnfavoriteEntry);
  }

  /**
   * Logs a search run activity
   * @param uid - The user ID
   * @param searchCriteria - The search criteria used
   */
  async logSearchRun(uid: string, searchCriteria: string): Promise<void> {
    const searchRunEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'Search Run',
      actionSubject: searchCriteria
    };
    
    await this.logActivity(searchRunEntry);
  }

  /**
   * Logs a grade update activity
   * @param uid - The user ID
   * @param courseCount - The total number of courses with grades after the update
   */
  async logGradeUpdate(uid: string, courseCount: number): Promise<void> {
    const gradeUpdateEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'Updated Grades',
      actionSubject: `${courseCount} courses with grades`
    };
    
    await this.logActivity(gradeUpdateEntry);
  }

  /**
   * Logs a favorite school rank upgrade activity
   * @param uid - The user ID
   * @param schoolId - The school ID that was moved up in rank
   */
  async logFavoriteSchoolRankUpgrade(uid: string, schoolId: string): Promise<void> {
    const rankUpgradeEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'Upgraded Favourite School Rank',
      actionSubject: schoolId
    };
    
    await this.logActivity(rankUpgradeEntry);
  }

  /**
   * Logs a favorite school rank downgrade activity
   * @param uid - The user ID
   * @param schoolId - The school ID that was moved down in rank
   */
  async logFavoriteSchoolRankDowngrade(uid: string, schoolId: string): Promise<void> {
    const rankDowngradeEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'Downgraded Favourite School Rank',
      actionSubject: schoolId
    };
    
    await this.logActivity(rankDowngradeEntry);
  }

  /**
   * Logs a favorite program rank upgrade activity
   * @param uid - The user ID
   * @param programId - The program ID that was moved up in rank
   */
  async logFavoriteProgramRankUpgrade(uid: string, programId: string): Promise<void> {
    const rankUpgradeEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'Upgraded Favourite Program Rank',
      actionSubject: programId
    };
    
    await this.logActivity(rankUpgradeEntry);
  }

  /**
   * Logs a favorite program rank downgrade activity
   * @param uid - The user ID
   * @param programId - The program ID that was moved down in rank
   */
  async logFavoriteProgramRankDowngrade(uid: string, programId: string): Promise<void> {
    const rankDowngradeEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'Downgraded Favourite Program Rank',
      actionSubject: programId
    };
    
    await this.logActivity(rankDowngradeEntry);
  }

  /**
   * Logs an event pageview activity
   * @param uid - The user ID
   * @param eventId - The event ID being viewed
   */
  async logEventPageview(uid: string, eventId: string): Promise<void> {
    const eventPageviewEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'Event Pageview',
      actionSubject: eventId
    };
    
    await this.logActivity(eventPageviewEntry);
  }

  /**
   * Logs an event added to calendar activity
   * @param uid - The user ID
   * @param eventId - The event ID being added to calendar
   */
  async logEventAddedToCalendar(uid: string, eventId: string): Promise<void> {
    const eventCalendarEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'Added Event to Calendar',
      actionSubject: eventId
    };
    
    await this.logActivity(eventCalendarEntry);
  }

  /**
   * Logs an event removed from calendar activity
   * @param uid - The user ID
   * @param eventId - The event ID being removed from calendar
   */
  async logEventRemovedFromCalendar(uid: string, eventId: string): Promise<void> {
    const eventCalendarEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'Removed Event from Calendar',
      actionSubject: eventId
    };
    
    await this.logActivity(eventCalendarEntry);
  }

  /**
   * Logs a program category pageview activity
   * @param uid - The user ID
   * @param categoryName - The program category name being viewed
   */
  async logProgramCategoryPageview(uid: string, categoryName: string): Promise<void> {
    const programCategoryEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'Program Category Pageview',
      actionSubject: categoryName
    };
    
    await this.logActivity(programCategoryEntry);
  }

  /**
   * Logs an external program website referral activity
   * @param uid - The user ID
   * @param programId - The program ID for the external website referral
   */
  async logExternalProgramWebsiteReferral(uid: string, programId: string): Promise<void> {
    const externalWebsiteEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'External Program Website Referral',
      actionSubject: programId
    };
    
    await this.logActivity(externalWebsiteEntry);
  }

  /**
   * Logs an external school website referral activity
   * @param uid - The user ID
   * @param schoolId - The school ID for the external website referral
   */
  async logExternalSchoolWebsiteReferral(uid: string, schoolId: string): Promise<void> {
    const externalWebsiteEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'External School Website Referral',
      actionSubject: schoolId
    };
    
    await this.logActivity(externalWebsiteEntry);
  }

  /**
   * Logs an external athletics website referral activity
   * @param uid - The user ID
   * @param schoolId - The school ID for the external athletics website referral
   */
  async logExternalAthleticsWebsiteReferral(uid: string, schoolId: string): Promise<void> {
    const externalWebsiteEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'External Athletics Website Referral',
      actionSubject: schoolId
    };
    
    await this.logActivity(externalWebsiteEntry);
  }

  /**
   * Logs an external school Twitter referral activity
   * @param uid - The user ID
   * @param schoolId - The school ID for the external Twitter referral
   */
  async logExternalSchoolTwitterReferral(uid: string, schoolId: string): Promise<void> {
    const externalTwitterEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'External School Twitter Referral',
      actionSubject: schoolId
    };
    
    await this.logActivity(externalTwitterEntry);
  }

  /**
   * Logs an external school Instagram referral activity
   * @param uid - The user ID
   * @param schoolId - The school ID for the external Instagram referral
   */
  async logExternalSchoolInstagramReferral(uid: string, schoolId: string): Promise<void> {
    const externalInstagramEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'External School Instagram Referral',
      actionSubject: schoolId
    };
    
    await this.logActivity(externalInstagramEntry);
  }

  /**
   * Logs an external school Wikipedia referral activity
   * @param uid - The user ID
   * @param schoolId - The school ID for the external Wikipedia referral
   */
  async logExternalSchoolWikipediaReferral(uid: string, schoolId: string): Promise<void> {
    const externalWikipediaEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'External School Wikipedia Referral',
      actionSubject: schoolId
    };
    
    await this.logActivity(externalWikipediaEntry);
  }

  /**
   * Logs an external school YouTube referral activity
   * @param uid - The user ID
   * @param schoolId - The school ID for the external YouTube referral
   */
  async logExternalSchoolYouTubeReferral(uid: string, schoolId: string): Promise<void> {
    const externalYouTubeEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'External School YouTube Referral',
      actionSubject: schoolId
    };
    
    await this.logActivity(externalYouTubeEntry);
  }

  /**
   * Logs an external event website referral activity
   * @param uid - The user ID
   * @param eventId - The event ID for the external website referral
   */
  async logExternalEventWebsiteReferral(uid: string, eventId: string): Promise<void> {
    const externalWebsiteEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'External Event Website Referral',
      actionSubject: eventId
    };
    
    await this.logActivity(externalWebsiteEntry);
  }

  /**
   * Logs an eligibility calculation activity
   * @param uid - The user ID
   * @param calculationType - Either "Local" or "API" depending on the calculation type
   */
  async logEligibilityCalculation(uid: string, calculationType: 'Local' | 'API'): Promise<void> {
    const eligibilityEntry: ActivityLogEntry = {
      timestamp: Date.now(),
      uid: uid,
      actionTaken: 'Eligibility Calculation',
      actionSubject: calculationType
    };
    
    await this.logActivity(eligibilityEntry);
  }


}

export const activityLogService = new ActivityLogService();
